import React from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    Grid,
    Card,
    Autocomplete,
    TextField,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import CBackDrop from "../../../basecomp/components/CBackDrop";
import {
    Shield,
    Settings,
    Security
} from '@mui/icons-material';
import PermissionVM from "../viewmodel/PermissionVM";

const PermissionView = () => {
    const {
        permissionsData,
        permissionType,
        selectedTarget,
        checksList,
        isLoading,
        dataDisplay,
        handlePermissionTypeChange,
        handleTargetChange,
        handlePermissionChange,
        handleBatchPermissionChange,
        handleSubmitPermissions,
        getDataListByType,
        isPermissionChecked,
    } = PermissionVM();

    const getPermissionTypeLabel = (type) => {
        switch (type) {
            case 'User': return 'Email';
            // case 'UserType': return 'Role';
            // case 'UserTerminal': return 'Terminal';
            default: return 'Select';
        }
    };

    const getSelectedDisplayValue = () => {
        const dataList = getDataListByType();
        const selected = dataList.find(item => item.id == selectedTarget);
        return selected ? selected.display_val : '';
    };

    // Helper function to get label after dash
    const getLabelAfterDash = (label) => {
        if (!label) return "";
        const dashIndex = label.indexOf("-");
        return dashIndex === -1 ? label : label.substring(dashIndex + 1).trim();
    };

    // Helper function to get tool name from permission key
    const getToolNameFromKey = (key) => {
        const display = dataDisplay[key];
        if (!display) return '';

        // Extract tool name from patterns like "tool_toolname - description"
        if (display.startsWith('tool_')) {
            const afterTool = display.substring(5); // Remove 'tool_'
            const dashIndex = afterTool.indexOf(' - ');
            return dashIndex !== -1 ? afterTool.substring(0, dashIndex) : afterTool;
        }

        // Extract from menu_ or feature_ patterns
        const match = display.match(/(menu_|feature_)([^_]+)_/);
        return match ? match[2] : '';
    };

    // Group permissions by tool
    const groupPermissionsByTool = () => {
        const groups = {};

        Object.keys(dataDisplay).forEach(key => {
            const display = dataDisplay[key];
            const toolName = getToolNameFromKey(key);

            if (!toolName) return;

            if (!groups[toolName]) {
                groups[toolName] = {
                    toolKey: null,
                    menuItems: [],
                    featureItems: []
                };
            }

            if (display.startsWith('tool_')) {
                groups[toolName].toolKey = key;
            } else if (display.startsWith('menu_')) {
                groups[toolName].menuItems.push(key);
            } else if (display.startsWith('feature_')) {
                groups[toolName].featureItems.push(key);
            }
        });

        return groups;
    };

    // Handle tool header change - check/uncheck all items in the tool
    const handleToolHeaderChange = (toolName, isChecked) => {
        const group = groupedPermissions[toolName];
        if (!group) return;

        const allKeys = [
            group.toolKey,
            ...group.menuItems,
            ...group.featureItems
        ].filter(Boolean);

        handleBatchPermissionChange(allKeys, isChecked);
    };

    // Check if tool header should be checked (any item in tool is checked)
    const isToolHeaderChecked = (toolName) => {
        const group = groupedPermissions[toolName];
        if (!group) return false;

        const allKeys = [
            group.toolKey,
            ...group.menuItems,
            ...group.featureItems
        ].filter(Boolean);

        return allKeys.some(key => isPermissionChecked(key));
    };

    const groupedPermissions = groupPermissionsByTool();

    return (
        <>
            <CBackDrop
                cOpen={isLoading}
                cHandleClose={() => { }}
                cAllowClickToClose={false}
            />
            <Box sx={{
                minHeight: '100vh',
                background: 'rgb(254, 254, 254)',
                pt: { xs: '60px', sm: '70px', md: '84px' },
                pb: 4,
                px: { xs: 1, sm: 2, md: 4, lg: 8 }
            }}>
                <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                    <Paper
                        elevation={4}
                        sx={{
                            borderRadius: '16px',
                            overflow: 'hidden',
                            border: '1px solid rgba(60, 176, 67, 0.2)',
                            marginTop: "1.5em",
                            boxShadow: '0 10px 30px rgba(25, 25, 112, 0.1)'
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                background: '#3CB043',
                                p: { xs: 2, sm: 3, md: 3.5 },
                                color: 'white',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                                    pointerEvents: 'none'
                                }
                            }}
                        >
                            <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Security sx={{ fontSize: 28, color: 'white' }} />
                                        <Typography variant="body1" sx={{
                                            color: 'rgba(255, 255, 255, 0.95)',
                                            fontWeight: 900,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                            flexGrow: 1
                                        }}>
                                            Manage user permissions and access controls - Configure system access rights and feature permissions
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                            {/* Selection Controls */}
                            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={[
                                            { value: 'User', label: 'User' },
                                            // { value: 'UserType', label: 'User Type' },
                                            // { value: 'UserTerminal', label: 'User Terminal' }
                                        ]}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={permissionType ? {
                                            value: permissionType,
                                            label: permissionType === 'UserType' ? 'User Type' :
                                                permissionType === 'UserTerminal' ? 'User Terminal' : 'User'
                                        } : null}
                                        onChange={(event, newValue) => {
                                            handlePermissionTypeChange(newValue ? newValue.value : "");
                                        }}
                                        clearIcon={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#e2e8f0',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: '#cbd5e1',
                                                    }
                                                }}
                                            >
                                                ×
                                            </Box>
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Choose Permission Type"
                                                placeholder="Select Permission Type"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        height: 52,
                                                        fontSize: '0.95rem',
                                                        backgroundColor: 'white',
                                                        borderRadius: '3px',
                                                        '& fieldset': {
                                                            borderColor: 'rgba(4, 37, 96, 0.2)',
                                                            borderWidth: '2px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#3CB043',
                                                            borderWidth: '2px'
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#042560',
                                                            borderWidth: '2px'
                                                        },
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        fontSize: '0.95rem',
                                                        color: '#042560',
                                                        fontWeight: 500,
                                                        '&.Mui-focused': {
                                                            color: '#042560',
                                                            fontWeight: 600
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const { key, ...otherProps } = props;
                                            return (
                                                <Box component="li" key={key} {...otherProps}>
                                                    <Typography variant="body1">
                                                        {option.label}
                                                    </Typography>
                                                </Box>
                                            );
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={getDataListByType()}
                                        getOptionLabel={(option) => option.display_val || ""}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={getDataListByType().find(item => item.id == selectedTarget) || null}
                                        onChange={(event, newValue) => {
                                            handleTargetChange(newValue ? newValue.id : "");
                                        }}
                                        disabled={!permissionType || getDataListByType().length === 0 || isLoading}
                                        loading={isLoading}
                                        clearIcon={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#e2e8f0',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: '#cbd5e1',
                                                    }
                                                }}
                                            >
                                                ×
                                            </Box>
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={permissionType ? `Choose ${getPermissionTypeLabel(permissionType)}` : "Select Permission Type First"}
                                                placeholder={permissionType ? `Select ${getPermissionTypeLabel(permissionType)}` : ""}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        height: 52,
                                                        fontSize: '0.95rem',
                                                        backgroundColor: 'white',
                                                        borderRadius: '3px',
                                                        '& fieldset': {
                                                            borderColor: 'rgba(4, 37, 96, 0.2)',
                                                            borderWidth: '2px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#3CB043',
                                                            borderWidth: '2px'
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#042560',
                                                            borderWidth: '2px'
                                                        },
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        fontSize: '0.95rem',
                                                        color: '#042560',
                                                        fontWeight: 500,
                                                        '&.Mui-focused': {
                                                            color: '#042560',
                                                            fontWeight: 600
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const { key, ...otherProps } = props;
                                            return (
                                                <Box component="li" key={key} {...otherProps}>
                                                    <Typography variant="body1">
                                                        {option.display_val}
                                                    </Typography>
                                                </Box>
                                            );
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Loading indicator */}
                            {isLoading && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 6,
                                    backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                    borderRadius: '3px',
                                    mb: 3
                                }}>
                                    <Typography variant="body2" sx={{
                                        ml: 2,
                                        color: '#374151',
                                        fontWeight: 500,
                                        fontSize: '0.95rem'
                                    }}>
                                        Loading {getPermissionTypeLabel(permissionType).toLowerCase()}s...
                                    </Typography>
                                </Box>
                            )}

                            {/* Permissions Cards - Only show when target is selected and data exists */}
                            {selectedTarget && dataDisplay && Object.keys(dataDisplay).length > 0 && (
                                <Box>
                                    <Paper
                                        sx={{
                                            background: 'linear-gradient(135deg, #f0fdf4 0%, #f0f9ff 100%)',
                                            p: { xs: 1, sm: 1.5 },
                                            mb: 3,
                                            borderRadius: '3px',
                                            border: '2px solid rgba(60, 176, 67, 0.1)'
                                        }}
                                    >
                                        <Typography variant="h6" sx={{
                                            fontWeight: 700,
                                            color: '#042560',
                                            m: 0,
                                            fontSize: { xs: '1rem', sm: '1.1rem' }
                                        }}>
                                            Permissions for: <Box component="span" sx={{
                                                color: '#3CB043',
                                                fontWeight: 600
                                            }}>
                                                {getSelectedDisplayValue()}
                                            </Box>
                                        </Typography>
                                    </Paper>

                                    {/* Permission Cards Grid */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        gap: '1em'
                                    }}>
                                        {Object.entries(groupedPermissions).map(([toolName, group]) => (
                                            <Card
                                                key={toolName}
                                                variant="outlined"
                                                sx={{
                                                    width: "24%",
                                                    minWidth: "280px",
                                                    height: "570px", // Fixed height for all cards
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    backgroundColor: "#fff",
                                                    borderRadius: '3px',
                                                    border: '2px solid rgba(60, 176, 67, 0.1)',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                                                        transform: 'translateY(-2px)'
                                                    }
                                                }}
                                            >
                                                {/* Tool Header - Fixed height */}
                                                <Box
                                                    sx={{
                                                        bgcolor: '#f8fafc',
                                                        px: 2,
                                                        py: 1.5,
                                                        borderBottom: '2px solid rgba(60, 176, 67, 0.1)',
                                                        borderRadius: '12px 12px 0 0',
                                                        minHeight: '70px', // Fixed header height
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isToolHeaderChecked(toolName)}
                                                                onChange={(e) => handleToolHeaderChange(toolName, e.target.checked)}
                                                                sx={{
                                                                    color: '#3CB043',
                                                                    transform: 'scale(1.2)',
                                                                    '&.Mui-checked': {
                                                                        color: '#3CB043',
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Settings sx={{ color: '#3CB043', fontSize: 20 }} />
                                                                <Typography sx={{
                                                                    fontWeight: 700,
                                                                    color: '#042560',
                                                                    fontSize: '1.1rem',
                                                                    lineHeight: 1.2,
                                                                    wordBreak: 'break-word'
                                                                }}>
                                                                    {group.toolKey ? getLabelAfterDash(dataDisplay[group.toolKey]) : toolName}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        sx={{ m: 0, width: '100%' }}
                                                    />
                                                </Box>

                                                {/* Content - Scrollable area */}
                                                <Box
                                                    sx={{
                                                        px: 2,
                                                        py: 1.5,
                                                        flex: 1,
                                                        overflow: 'auto',
                                                        height: 'calc(400px - 70px)', // Remaining height after header
                                                        '&::-webkit-scrollbar': {
                                                            width: '6px',
                                                        },
                                                        '&::-webkit-scrollbar-track': {
                                                            background: '#f1f5f9',
                                                            borderRadius: '3px',
                                                        },
                                                        '&::-webkit-scrollbar-thumb': {
                                                            background: '#cbd5e1',
                                                            borderRadius: '3px',
                                                            '&:hover': {
                                                                background: '#94a3b8',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {/* Menu Section */}
                                                    {group.menuItems.length > 0 && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: '#042560',
                                                                    mb: 1,
                                                                    fontSize: '1rem',
                                                                    position: 'sticky',
                                                                    top: 0,
                                                                    backgroundColor: '#fff',
                                                                    py: 0.5,
                                                                    zIndex: 1
                                                                }}
                                                            >
                                                                Menu
                                                            </Typography>
                                                            {group.menuItems.map((item) => (
                                                                <Box key={item} sx={{ mb: 0.5 }}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={isPermissionChecked(item)}
                                                                                onChange={(e) => handlePermissionChange(item, e.target.checked)}
                                                                                sx={{
                                                                                    color: '#3CB043',
                                                                                    transform: 'scale(1.1)',
                                                                                    '&.Mui-checked': {
                                                                                        color: '#3CB043',
                                                                                    }
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Typography sx={{
                                                                                fontSize: '1em',
                                                                                color: '#374151',
                                                                                fontWeight: 500,
                                                                                lineHeight: 1.3,
                                                                                wordBreak: 'break-word'
                                                                            }}>
                                                                                {getLabelAfterDash(dataDisplay[item])}
                                                                            </Typography>
                                                                        }
                                                                        sx={{
                                                                            m: 0,
                                                                            width: '100%',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(60, 176, 67, 0.05)'
                                                                            },
                                                                            borderRadius: '6px',
                                                                            px: 1,
                                                                            py: 0.25
                                                                        }}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}

                                                    {/* Feature Section */}
                                                    {group.featureItems.length > 0 && (
                                                        <Box>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: '#3CB043',
                                                                    mb: 1,
                                                                    fontSize: '1rem',
                                                                    position: 'sticky',
                                                                    top: 0,
                                                                    backgroundColor: '#fff',
                                                                    py: 0.5,
                                                                    zIndex: 1
                                                                }}
                                                            >
                                                                Feature
                                                            </Typography>
                                                            {group.featureItems.map((item) => (
                                                                <Box key={item} sx={{ mb: 0.5 }}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={isPermissionChecked(item)}
                                                                                onChange={(e) => handlePermissionChange(item, e.target.checked)}
                                                                                sx={{
                                                                                    color: '#3CB043',
                                                                                    transform: 'scale(1.1)',
                                                                                    '&.Mui-checked': {
                                                                                        color: '#3CB043',
                                                                                    }
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Typography sx={{
                                                                                fontSize: '1em',
                                                                                color: '#374151',
                                                                                fontWeight: 500,
                                                                                lineHeight: 1.3,
                                                                                wordBreak: 'break-word'
                                                                            }}>
                                                                                {getLabelAfterDash(dataDisplay[item])}
                                                                            </Typography>
                                                                        }
                                                                        sx={{
                                                                            m: 0,
                                                                            width: '100%',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(60, 176, 67, 0.05)'
                                                                            },
                                                                            borderRadius: '6px',
                                                                            px: 1,
                                                                            py: 0.25
                                                                        }}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}

                                                    {/* Empty state */}
                                                    {group.menuItems.length === 0 && group.featureItems.length === 0 && (
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            height: '100%',
                                                            textAlign: 'center'
                                                        }}>
                                                            <Typography sx={{
                                                                color: '#9ca3af',
                                                                fontStyle: 'italic',
                                                                fontSize: '0.9rem'
                                                            }}>
                                                                No menu or feature permissions available
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Card>
                                        ))}
                                    </Box>

                                    {/* Submit Button */}
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleSubmitPermissions}
                                            size="large"
                                            disabled={!selectedTarget}
                                            startIcon={<Shield />}
                                            sx={{
                                                backgroundColor: '#042560',
                                                color: '#ffffff',
                                                px: { xs: 3, sm: 4 },
                                                py: { xs: 1.5, sm: 2 },
                                                borderRadius: '3px',
                                                fontWeight: 600,
                                                fontSize: { xs: '0.95rem', sm: '1rem' },
                                                boxShadow: '0 4px 12px rgba(4, 37, 96, 0.3)',
                                                textTransform: 'none',
                                                border: '2px solid rgba(255,255,255,0.2)',
                                                '&:hover': {
                                                    backgroundColor: '#031e4d',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 6px 16px rgba(4, 37, 96, 0.4)',
                                                },
                                                '&:disabled': {
                                                    backgroundColor: '#9ca3af',
                                                    color: '#6b7280',
                                                    transform: 'none',
                                                    boxShadow: 'none',
                                                    border: '2px solid rgba(156, 163, 175, 0.2)',
                                                },
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            Submit Permissions
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* No permissions data message */}
                            {selectedTarget && (!dataDisplay || Object.keys(dataDisplay).length === 0) && (
                                <Box sx={{
                                    textAlign: 'center',
                                    py: 6,
                                    backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                    borderRadius: '3px',
                                    border: '2px dashed rgba(156, 163, 175, 0.3)'
                                }}>
                                    <Security sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                                    <Typography variant="h6" sx={{
                                        fontWeight: 600,
                                        color: '#374151',
                                        mb: 1,
                                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                    }}>
                                        No permissions data available
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: '#6b7280',
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}>
                                        Please check your configuration or contact administrator.
                                    </Typography>
                                </Box>
                            )}

                            {/* Message when no target is selected but permission type is selected */}
                            {permissionType && !selectedTarget && getDataListByType().length > 0 && !isLoading && (
                                <Box sx={{
                                    textAlign: 'center',
                                    py: 6,
                                    backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                    borderRadius: '3px',
                                    border: '2px dashed rgba(60, 176, 67, 0.3)'
                                }}>
                                    <Security sx={{ fontSize: 48, color: '#3CB043', mb: 2 }} />
                                    <Typography variant="h6" sx={{
                                        fontWeight: 600,
                                        color: '#042560',
                                        mb: 1,
                                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                    }}>
                                        Select {getPermissionTypeLabel(permissionType)}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: '#6b7280',
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}>
                                        Please select a {getPermissionTypeLabel(permissionType).toLowerCase()} to manage permissions.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </>
    );
};

export default PermissionView;